import { useCallback, useEffect, useRef, useState } from 'react';
import userService from 'services/user';
import {
    assignmentDefaultsFromApi,
    assignmentFormFromApi,
    assignmentImportPatch,
    createAssignmentForm,
} from 'utils/assignmentDefaults';
import { v4 as uuid } from 'uuid';

const useAssignmentEditorSession = ({
    auth,
    course,
    courseId,
    currentAssignment,
    isNewRoute,
    importedAssignment,
    relatedDataReady,
}) => {
    const creationIdRef = useRef(uuid());
    const persistedAssignmentRef = useRef(null);
    const hydratedAssignmentIdRef = useRef(null);
    const hydrationFormRef = useRef(null);
    const hydrationLastSavedAtRef = useRef(null);
    const appliedPreferencesKey = useRef(null);
    const [form, setForm] = useState(() => createAssignmentForm({ gradingSettings: null }));
    const formRef = useRef(form);
    const [errors, setErrors] = useState({});
    const [preferencesReady, setPreferencesReady] = useState(!isNewRoute);
    const [importedDraft, setImportedDraft] = useState(false);
    const [persistedAssignment, setPersistedAssignment] = useState(null);
    const [hydrationRevision, setHydrationRevision] = useState(0);
    const [isPromoting, setIsPromoting] = useState(false);
    formRef.current = form;

    const markPersisted = useCallback(
        (saved) => {
            if (isNewRoute && saved?.id) setIsPromoting(true);
        },
        [isNewRoute]
    );

    useEffect(() => {
        if (!isNewRoute) return;
        if (!course || !courseId || !auth?.user_id) {
            setPreferencesReady(true);
            return;
        }
        const preferenceKey = `${auth.user_id}:${courseId}`;
        if (appliedPreferencesKey.current === preferenceKey) {
            setPreferencesReady(true);
            return;
        }
        appliedPreferencesKey.current = preferenceKey;
        let active = true;
        (async () => {
            let preferences;
            try {
                preferences = await userService.getAssignmentDefaults(auth.user_id);
            } catch (error) {
                preferences = null;
            }
            if (!active) return;
            const defaults = assignmentDefaultsFromApi(preferences);
            setForm((current) => ({
                ...current,
                aiGradingEnabled: defaults.aiGradingEnabled,
                gradingCustomContext: defaults.gradingCustomContext,
                aiFeedbackEnabled: defaults.aiFeedbackEnabled,
                feedbackSettings: { ...current.feedbackSettings, ...defaults.feedbackSettings },
                feedbackCustomContext: defaults.feedbackCustomContext,
                aiStudentFeedbackEnabled: defaults.aiStudentFeedbackEnabled,
                studentFeedbackSettings: { ...current.studentFeedbackSettings, ...defaults.studentFeedbackSettings },
                studentFeedbackCustomContext: defaults.studentFeedbackCustomContext,
                aiInlineCommentsEnabled: defaults.aiInlineCommentsEnabled,
                inlineCommentsSettings: { ...current.inlineCommentsSettings, ...defaults.inlineCommentsSettings },
                inlineCommentsCustomContext: defaults.inlineCommentsCustomContext,
            }));
            setPreferencesReady(true);
        })();
        return () => {
            active = false;
        };
    }, [auth?.user_id, course, courseId, isNewRoute]);

    useEffect(() => {
        if (isNewRoute) {
            if (hydratedAssignmentIdRef.current !== 'new') {
                hydratedAssignmentIdRef.current = 'new';
                creationIdRef.current = uuid();
                persistedAssignmentRef.current = null;
                setPersistedAssignment(null);
                const nextForm = createAssignmentForm({ gradingSettings: null });
                hydrationFormRef.current = nextForm;
                hydrationLastSavedAtRef.current = null;
                setForm(nextForm);
                setErrors({});
                setPreferencesReady(Boolean(!course || !courseId || !auth?.user_id));
                setImportedDraft(false);
                setIsPromoting(false);
                appliedPreferencesKey.current = null;
                setHydrationRevision((revision) => revision + 1);
            }
            return;
        }
        if (!currentAssignment || hydratedAssignmentIdRef.current === currentAssignment.id) return;
        if (persistedAssignmentRef.current?.id === currentAssignment.id) {
            hydratedAssignmentIdRef.current = currentAssignment.id;
            hydrationFormRef.current = formRef.current;
            hydrationLastSavedAtRef.current = currentAssignment.modified_on;
            setPersistedAssignment(currentAssignment);
            return;
        }
        hydratedAssignmentIdRef.current = currentAssignment.id;
        setIsPromoting(false);
        persistedAssignmentRef.current = currentAssignment;
        setPersistedAssignment(currentAssignment);
        const nextForm = assignmentFormFromApi(currentAssignment);
        hydrationFormRef.current = nextForm;
        hydrationLastSavedAtRef.current = currentAssignment.modified_on;
        setForm(nextForm);
        setErrors({});
        setHydrationRevision((revision) => revision + 1);
    }, [auth?.user_id, course, courseId, currentAssignment, isNewRoute]);

    useEffect(() => {
        if (!isNewRoute || !importedAssignment) return;
        setForm((current) => ({ ...current, ...assignmentImportPatch(importedAssignment) }));
        setImportedDraft(true);
    }, [importedAssignment, isNewRoute]);

    useEffect(() => {
        if (isPromoting && !isNewRoute && relatedDataReady) setIsPromoting(false);
    }, [isNewRoute, isPromoting, relatedDataReady]);

    return {
        creationIdRef,
        errors,
        form,
        hydrationFormRef,
        hydrationLastSavedAtRef,
        hydrationRevision,
        importedDraft,
        isEditorReady:
            Boolean(form) &&
            (isNewRoute
                ? preferencesReady
                : isPromoting ||
                  Boolean(
                      currentAssignment?.id &&
                          hydratedAssignmentIdRef.current === currentAssignment.id &&
                          relatedDataReady
                  )),
        markPersisted,
        persistedAssignment,
        persistedAssignmentRef,
        preferencesReady,
        setErrors,
        setForm,
        setImportedDraft,
        setPersistedAssignment,
    };
};

export default useAssignmentEditorSession;
